import { ShieldAlert, ChevronUp, ChevronDown } from "lucide-react";

/**
 * Shopify Audit results panel — rendered inside AdminProducts.
 * Shows a summary of flagged products and per-row delete action.
 */
export default function ShopifyAuditPanel({
  auditOpen,
  auditLoading,
  auditData,
  deletingId,
  onToggle,
  onDelete,
}) {
  if (!auditOpen) return null;

  return (
    <div className="mb-4 border border-white/[0.06] rounded overflow-hidden" data-testid="shopify-audit-panel">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
      >
        <div className="flex items-center gap-2">
          <ShieldAlert size={13} className="text-white/40" />
          <span className="text-xs font-semibold text-white">Shopify Product Audit</span>
          {auditData && !auditData.error && (
            <span className="text-[10px] text-white/30">
              {auditData.total_shopify} total · {auditData.flagged?.length || 0} flagged
            </span>
          )}
        </div>
        {auditOpen ? <ChevronUp size={12} className="text-white/30" /> : <ChevronDown size={12} className="text-white/30" />}
      </button>
      <div className="p-3">
        {auditLoading && <p className="text-xs text-white/40 text-center py-4">Running audit…</p>}
        {auditData?.error && <p className="text-xs text-red-400">{auditData.error}</p>}
        {auditData && !auditData.error && (
          <>
            <div className="flex gap-3 mb-3 flex-wrap">
              {Object.entries(auditData.summary || {}).map(([k, v]) => (
                <span
                  key={k}
                  className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${v > 0 ? "bg-red-500/10 border-red-500/20 text-red-400" : "bg-white/5 border-white/10 text-white/25"}`}
                >
                  {k.replace(/_/g, " ")}: {v}
                </span>
              ))}
            </div>
            {auditData.flagged?.length === 0 && (
              <p className="text-xs text-green-400/70 text-center py-2">All Shopify products look clean.</p>
            )}
            <div className="space-y-1.5">
              {(auditData.flagged || []).map((item) => (
                <AuditFlaggedRow
                  key={item.shopify_id}
                  item={item}
                  deleting={deletingId === item.shopify_id}
                  onDelete={() => onDelete(item.shopify_id)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AuditFlaggedRow({ item, deleting, onDelete }) {
  const statusClass = item.status === "draft" ? "bg-yellow-500/15 text-yellow-400" : "bg-white/5 text-white/30";
  return (
    <div
      className="flex items-start gap-2 p-2.5 border border-white/[0.05] rounded bg-white/[0.01]"
      data-testid={`audit-item-${item.shopify_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-white/70 font-medium truncate">{item.title}</span>
          <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${statusClass}`}>{item.status}</span>
          {(item.issues || []).map((iss) => (
            <span key={iss} className="text-[9px] font-bold px-1 py-0.5 rounded bg-red-500/10 text-red-400">
              {iss.replace(/_/g, " ")}
            </span>
          ))}
        </div>
        <p className="text-[10px] text-white/25 mt-0.5">
          {item.product_type} · {item.variant_count} variant{item.variant_count !== 1 ? "s" : ""} · £{item.prices?.[0] || "?"}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <a
          href={item.shopify_admin_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] text-white/25 hover:text-white/60 underline transition-colors"
        >
          View
        </a>
        <button
          onClick={onDelete}
          disabled={deleting}
          className="text-[10px] px-2 py-1 rounded border border-red-500/20 text-red-400/70 hover:text-red-400 hover:border-red-500/40 disabled:opacity-40 transition-colors"
          data-testid={`delete-shopify-${item.shopify_id}`}
        >
          {deleting ? "…" : "Delete"}
        </button>
      </div>
    </div>
  );
}
